import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { Button, IconButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../../../assets/styles/tokens";
import CloseIcon from "@mui/icons-material/Close";
import { Stack } from "@mui/system";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PercentIcon from "@mui/icons-material/Percent";

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

export default function AccountSummeryCategorySortBy({
  sortConfig,
  setSortConfig,
}: any) {
  const [open, setOpen] = React.useState(false);
  const [tempField, setTempField] = React.useState<string | null>(
    sortConfig.field,
  );
  const [tempDirection, setTempDirection] = React.useState<"asc" | "desc">(
    sortConfig.direction,
  );

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  React.useEffect(() => {
    if (open) {
      setTempField(sortConfig.field);
      setTempDirection(sortConfig.direction);
    }
  }, [open, sortConfig]);

  const options = [
    {
      label: "Date",
      value: "Date",
      icon: <ArrowUpwardIcon />,
    },
    { label: "Spend", value: "spend" },
    { label: "CPC", value: "cpc" },
    { label: "Click Out", value: "click_out" },
    { label: "Click In", value: "click_in" },
    { label: "FTD BR", value: "br_ftd" },
    { label: "FTD NG", value: "ng_ftd" },
    { label: "FTD BR/NG", value: "ng_ftd_br_ftd" },
    { label: "QFTD", value: "qftds" },
    { label: "CVR", value: "cvr" },
    { label: "Total Income", value: "total_income_dollar" },
    { label: "CPA Income", value: "cpa_income_dollar" },
    { label: "RS Income", value: "rs_income_dollar" },
    { label: "Profit", value: "profit" },
    { label: "CAC", value: "cac" },
    { label: "CPA PV", value: "cpa_pv" },
    { label: "ROI", value: "roi_perc" },
    { label: "CPA PV QFTD", value: "cpa_pv_qftd" },
    { label: "EPC Click In", value: "epc_click_in" },
    {
      label: "RS %",
      value: "rs_perc_from_total_income",
      icon: <PercentIcon />,
    },
  ];

  const activeLabel =
    options.find((option) => option.value === sortConfig.field)?.label ??
    sortConfig.field;

  const ShortList = (
    <Box role="shortBy">
      <Puller />
      <Stack justifyContent={"end"} direction="row" p={1}>
        <IconButton
          aria-label="Logout"
          onClick={toggleDrawer(false)}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.sm.$value,
          color: colors.GrayLightMode[700].$value,
        }}
      >
        Sort By
      </Typography>

      <Box sx={{ my: 2 }}>
        {options.map(({ label, value }) => {
          const isSelected = tempField === value;
          const handleClick = () => {
            if (tempField !== value) {
              // New field selected → default desc
              setTempField(value);
              setTempDirection("desc");
            } else {
              // Same field clicked → toggle direction
              setTempDirection((prev) => (prev === "asc" ? "desc" : "asc"));
            }
          };

          return (
            <Button
              key={value}
              onClick={handleClick}
              startIcon={
                isSelected ? (
                  <ArrowUpwardIcon
                    sx={{
                      position: "absolute",
                      left: "7px",
                      top: tempDirection === "desc" ? "5px" : "5px",
                      width: spacing.Spacing.lg.$value,
                      transform:
                        tempDirection === "desc"
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                      transition: "0.2s",
                    }}
                  />
                ) : (
                  <>
                    <ArrowUpwardIcon
                      sx={{
                        position: "absolute",
                        left: "7px",
                        top: tempDirection === "desc" ? "5px" : "5px",
                        opacity: 0,
                        width: spacing.Spacing.lg.$value,
                      }}
                    />
                  </>
                )
              }
              variant={isSelected ? "outlined" : "outlined"}
              color="secondary"
              sx={{
                borderRadius: 2,
                mr: spacing.Spacing.sm.$value,
                mb: spacing.Spacing.sm.$value,
                py: spacing.Spacing.xs.$value,
                paddingRight: spacing.Spacing["1xl"].$value,
                paddingLeft: spacing.Spacing.xl.$value,
                textTransform: "capitalize",
                fontWeight: typography.Font_weight.Regular.$value,
                border: "1px solid" + colors.GrayLightMode[200].$value,
                color: colors.GrayLightMode[700].$value,
                fontSize: typography.Size.Text.xs.$value,
                ...(isSelected && {
                  border: "1px solid" + colors.Brand[600].$value + "!important",
                  boxShadow: "none !important",
                  color: colors.GrayLightMode[900].$value,
                }),
              }}
            >
              {label}
            </Button>
          );
        })}
      </Box>

      <Button
        fullWidth
        variant="contained"
        sx={{
          textTransform: "capitalize",
          boxShadow: "none !important",
          background: colors.Brand[600].$value,
          borderRadius: 2,
        }}
        disabled={!tempField}
        onClick={() => {
          setSortConfig({
            field: tempField,
            direction: tempDirection,
          });
          setOpen(false);
        }}
        data-tracker="847"
      >
        Apply
      </Button>
    </Box>
  );

  return (
    <>
      <Box
        onClick={toggleDrawer(true)}
        component="span"
        display={"flex"}
        alignItems={"center"}
        gap={0.5}
        sx={{
          cursor: "pointer",
          fontWeight: typography.Font_weight.Medium.$value,
          color: colors.Brand[700].$value,
        }}
        data-tracker="846"
      >
        {activeLabel}
        {sortConfig.direction === "asc" ? (
          <ArrowUpwardIcon
            sx={{
              color: colors.Brand[700].$value,
              fontSize: typography.Size.Text.xl.$value,
            }}
          />
        ) : (
          <ArrowDownwardIcon
            sx={{
              color: colors.Brand[700].$value,
              fontSize: typography.Size.Text.xl.$value,
            }}
          />
        )}
      </Box>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {ShortList}
      </Drawer>
    </>
  );
}
